import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./RelatedPosts.css";

const API_URL = process.env.REACT_APP_API_URL;
const AUTOPLAY_DELAY = 5000;
const CARD_GAP = 18;

function formatDate(date) {
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return date || "";

    return parsedDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function RelatedPosts({ currentSlug }) {
    const navigate = useNavigate();
    const viewportRef = useRef(null);
    const [posts, setPosts] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState(3);
    const [cardStep, setCardStep] = useState(0);
    const [timerVersion, setTimerVersion] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError(false);

        fetch(`${API_URL}/api/blogs`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error("Unable to load related posts.");
                return response.json();
            })
            .then((data) => {
                const latestPosts = (Array.isArray(data) ? [...data] : [])
                    .filter((post) => post.slug !== currentSlug)
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .slice(0, 5);

                setPosts(latestPosts);
                setCurrentIndex(0);
            })
            .catch((requestError) => {
                if (requestError.name !== "AbortError") {
                    console.error("Error fetching related posts:", requestError);
                    setError(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [currentSlug]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return undefined;

        const updateMeasurements = () => {
            const nextVisibleCount = window.innerWidth <= 700 ? 1 : 3;
            setVisibleCount(nextVisibleCount);
            setCardStep(
                (viewport.clientWidth - CARD_GAP * (nextVisibleCount - 1)) /
                    nextVisibleCount + CARD_GAP
            );
        };

        updateMeasurements();
        const observer = new ResizeObserver(updateMeasurements);
        observer.observe(viewport);
        window.addEventListener("resize", updateMeasurements);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateMeasurements);
        };
    }, [loading]);

    const slideCount = Math.max(1, posts.length - visibleCount + 1);

    useEffect(() => {
        setCurrentIndex((index) => Math.min(index, slideCount - 1));
    }, [slideCount]);

    useEffect(() => {
        if (slideCount <= 1) return undefined;

        const intervalId = window.setInterval(() => {
            setCurrentIndex((index) => (index + 1) % slideCount);
        }, AUTOPLAY_DELAY);

        return () => window.clearInterval(intervalId);
    }, [slideCount, timerVersion]);

    const goToSlide = useCallback((index) => {
        setCurrentIndex((index + slideCount) % slideCount);
        setTimerVersion((version) => version + 1);
    }, [slideCount]);

    if (!loading && (error || posts.length === 0)) return null;

    return (
        <section className="related-posts" aria-labelledby="related-posts-title">
            <h2 id="related-posts-title">Related Posts</h2>

            {loading ? (
                <p className="related-posts-status" aria-live="polite">
                    Loading related posts...
                </p>
            ) : (
                <div className="related-posts-carousel">
                    {slideCount > 1 && (
                        <button
                            type="button"
                            className="related-posts-arrow related-posts-arrow-previous"
                            onClick={() => goToSlide(currentIndex - 1)}
                            aria-label="Show previous related post"
                        >
                            <span aria-hidden="true">‹</span>
                        </button>
                    )}

                    <div className="related-posts-viewport" ref={viewportRef}>
                        <div
                            className="related-posts-track"
                            style={{ transform: `translate3d(-${currentIndex * cardStep}px, 0, 0)` }}
                        >
                            {posts.map((post) => (
                                <article className="related-post-card" key={post.slug}>
                                    <button
                                        type="button"
                                        className="related-post-image-link"
                                        onClick={() => navigate(`/blogs/${post.slug}`)}
                                        aria-label={`Read ${post.title}`}
                                    >
                                        <img
                                            src={require(`../images/${post.image}`)}
                                            alt={post.title}
                                        />
                                    </button>

                                    <div className="related-post-card-content">
                                        <div className="related-post-categories">
                                            {(post.categories ?? []).map((category) => (
                                                <span key={category}>{category}</span>
                                            ))}
                                        </div>

                                        <button
                                            type="button"
                                            className="related-post-title"
                                            onClick={() => navigate(`/blogs/${post.slug}`)}
                                        >
                                            {post.title}
                                        </button>

                                        <p className="related-post-meta">
                                            {post.author}
                                            {post.author && post.date && " | "}
                                            {formatDate(post.date)}
                                        </p>
                                        <p className="related-post-excerpt">{post.excerpt}</p>

                                        <button
                                            type="button"
                                            className="related-post-read-more"
                                            onClick={() => navigate(`/blogs/${post.slug}`)}
                                            aria-label={`Read more about ${post.title}`}
                                        >
                                            Read more <span aria-hidden="true">→</span>
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>

                    {slideCount > 1 && (
                        <button
                            type="button"
                            className="related-posts-arrow related-posts-arrow-next"
                            onClick={() => goToSlide(currentIndex + 1)}
                            aria-label="Show next related post"
                        >
                            <span aria-hidden="true">›</span>
                        </button>
                    )}

                    {slideCount > 1 && (
                        <div className="related-posts-pagination" aria-label="Choose related posts slide">
                            {Array.from({ length: slideCount }, (_, index) => (
                                <button
                                    type="button"
                                    key={index}
                                    className={currentIndex === index ? "active" : ""}
                                    onClick={() => goToSlide(index)}
                                    aria-label={`Go to related posts slide ${index + 1}`}
                                    aria-current={currentIndex === index ? "true" : undefined}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

export default RelatedPosts;
